import { useEffect, useRef } from 'react';
import { configureHarperClient, configureOnUnauthorized } from '../api/harperClient';
import { refresh as refreshTokens, AuthError } from '../api/authClient';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from '../context/SnackbarContext';

function decodeJwtExpiryMs(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) return null;
    const payload = JSON.parse(atob(parts[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Wires harperClient to AuthContext:
 *   - Provides the current access token (read-only) on every request.
 *   - On a server 401, silently refreshes via /AuthRefresh and hands the new
 *     token back to harperClient so the original call can be retried. If
 *     refresh fails, drops the session to `session_expired` and returns null
 *     (harperClient stops retrying and surfaces the 401).
 *
 * Concurrent 401s (many in-flight requests on the same expired access token)
 * are coalesced onto a single in-flight refresh promise so we don't hammer
 * /AuthRefresh or race multiple rotations of state.
 */
export function ApiClientSetup() {
  const {
    getAccessToken,
    getRefreshToken,
    updateAccessToken,
    markSessionExpired,
    unauthenticatedReason,
  } = useAuth();
  const { showSnackbar } = useSnackbar();

  const prevReasonRef = useRef<typeof unauthenticatedReason>(undefined);
  // Single flight for concurrent 401s — all callers await this promise, only
  // one POST to /AuthRefresh happens even if 20 requests 401 simultaneously.
  const inFlightRefreshRef = useRef<Promise<string | null> | null>(null);

  useEffect(() => {
    configureHarperClient(getAccessToken);

    const onUnauthorized = async (): Promise<string | null> => {
      if (inFlightRefreshRef.current) return inFlightRefreshRef.current;

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        markSessionExpired();
        return null;
      }

      const promise = (async () => {
        try {
          const res = await refreshTokens(refreshToken);
          const expiresAt = decodeJwtExpiryMs(res.access_token) ?? Date.now() + res.expires_in * 1000;
          updateAccessToken(res.access_token, expiresAt);
          return res.access_token;
        } catch (err) {
          // refresh_rejected / expired: drop the session. Network errors
          // also land here; the caller will see a 401 and render a retry
          // state, but we don't wipe localStorage so a later reload can
          // re-attempt the silent refresh.
          if (err instanceof AuthError && err.type === 'refresh_rejected') {
            markSessionExpired();
          }
          return null;
        } finally {
          inFlightRefreshRef.current = null;
        }
      })();

      inFlightRefreshRef.current = promise;
      return promise;
    };

    configureOnUnauthorized(onUnauthorized);
    return () => configureOnUnauthorized(null);
  }, [getAccessToken, getRefreshToken, updateAccessToken, markSessionExpired]);

  // Session-expired snackbar watcher — fires once per transition into
  // `session_expired`, ignores `manual_sign_out` (user chose to leave).
  useEffect(() => {
    if (
      unauthenticatedReason === 'session_expired' &&
      prevReasonRef.current !== 'session_expired'
    ) {
      showSnackbar(
        'Looks like you were signed out — sign back in to pick up where you left off.',
        { variant: 'error' },
      );
    }
    prevReasonRef.current = unauthenticatedReason;
  }, [unauthenticatedReason, showSnackbar]);

  return null;
}
